import React, { FC, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import { HistoryList, InfoContainer, Label } from './TwLoadShareHistory.styles';
import LoadsService from './../../providers/Loads/Loads.service';
import TwInitialsIndicator from './../TwInitialsIndicator/TwInitialsIndicator';
import DateUtils from './../../utils/dateUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import StringUtils from './../../utils/stringUtils';
import InvoicesService from '../../providers/Invoices/Invoices.service';
import { ShareChannel, ShareType } from '../../redux/api/load/load.model';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import { useLazyGetTripShareHistoryQuery } from '../../redux/api/trips/tripApi';
import { ShareHistory } from '../../redux/api/trips/trips.model';
import { useLazyGetSettlementShareHistoryQuery } from '../../redux/api/settlement/settlementApi';

export interface TwShareHistoryProps {
  id: string;
  shareType?: ShareType;
}

const useShareHistory = (id: string, shareType?: ShareType): [ShareHistory[], boolean] => {
  // For driver link and load update types 'id' is the load Id, for invoice history 'id' is invoice id
  const [history, setHistory] = useState<ShareHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useNotifications();
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [getTripShareHistory] = useLazyGetTripShareHistoryQuery();
  const [getSettlementShareHistory] = useLazyGetSettlementShareHistoryQuery();

  useEffect(() => {
    setIsLoading(true);
    setHistory([]);
    if (shareType === ShareType.invoice_history && id) {
      InvoicesService.getInvoiceHistory(id)
        .then((history) => {
          setHistory(history);
        })
        .catch((e) => {
          logRocketTrack('Invoice', {
            action: 'Resend invoice and view sent history',
            severity: 'medium',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Error getting invoice share history',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (shareType === ShareType.driver_settlement && id) {
      getSettlementShareHistory(id)
        .unwrap()
        .then((history) => {
          setHistory(history);
        })
        .catch((error) => {
          logRocketTrack('Settlements', {
            action: 'Email settlement info, view sent history',
            severity: 'medium',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Error getting settlement share history',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (shareType === ShareType.load_driver_link && enableLtl) {
      id &&
        getTripShareHistory(id)
          .unwrap()
          .then((history) => {
            setHistory(history);
          })
          .catch((error) => {
            logRocketTrack('Load Management', {
              action: 'Email load info, view sent history',
              severity: 'medium',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Error getting trip share history',
              variant: 'error'
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
    } else {
      id &&
        LoadsService.getShareHistory(id, shareType)
          .then((history) => {
            setHistory(history);
          })
          .catch((error) => {
            logRocketTrack('Load Management', {
              action: 'Email load info, view sent history',
              severity: 'medium',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Error getting share history',
              variant: 'error'
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
    }
  }, []);

  return [history, isLoading];
};

const TwShareHistory: FC<TwShareHistoryProps> = ({ id: loadId, shareType }) => {
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [history, isLoading] = useShareHistory(loadId, shareType);

  const getName = (row: ShareHistory): string => {
    if (shareType === ShareType.load_update && row?.customer?.name) {
      return row.customer.name;
    } else if (
      (shareType === ShareType.load_driver_link || shareType === ShareType.driver_settlement) &&
      row?.driver?.name
    ) {
      return row.driver.name;
    } else {
      return row.recipient_email ? row.recipient_email : '';
    }
  };

  const getChannelLabel = (row: ShareHistory): string => {
    if (row.channel === ShareChannel.email) {
      return 'Emailed on';
    } else {
      return `Text ${row.delivery_status ? row.delivery_status : 'sent'}`;
    }
  };

  const getContact = (row: ShareHistory): string => {
    if (!(row.customer || row.driver)) return '';
    if (row.channel === ShareChannel.email) {
      return row.recipient_email;
    } else {
      if (shareType === ShareType.load_update) {
        return row.customer.primary_phone
          ? StringUtils.formatPhoneNumber(row.customer.primary_phone)
          : '';
      } else {
        return row.driver.phone ? StringUtils.formatPhoneNumber(row.driver.phone) : '';
      }
    }
  };

  const showLabel = (): string => {
    let label = 'You have shared this order with';

    if (shareType === ShareType.invoice_history) {
      label = 'You have sent this invoice to:';
    } else if (shareType === ShareType.load_driver_link && enableLtl) {
      label = 'You have shared this trip with';
    } else if (shareType === ShareType.driver_settlement) {
      label = 'You have shared this settlement with';
    }

    return label;
  };

  const LoadingSkeleton: FC = () => {
    return (
      <HistoryList>
        {[...Array(2)].map((_, index) => {
          return (
            <InfoContainer
              key={index}
              spacing={2}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack spacing={1} direction="row" alignItems="center" width="60%">
                <Skeleton animation="wave" variant="circular" width={40} height={40} />

                <Stack spacing={0} width="50%">
                  <Skeleton animation="wave" height={15} width="80%" style={{ marginBottom: 6 }} />
                  <Skeleton animation="wave" height={15} width="80%" style={{ marginBottom: 6 }} />
                </Stack>
              </Stack>
              <Stack spacing={0} alignItems="flex-end" width="30%">
                <Skeleton animation="wave" height={15} width="80%" style={{ marginBottom: 6 }} />
                <Skeleton animation="wave" height={15} width="80%" style={{ marginBottom: 6 }} />
              </Stack>
            </InfoContainer>
          );
        })}
      </HistoryList>
    );
  };

  return (
    <>
      <Label>{history?.length > 0 && showLabel()} </Label>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <HistoryList>
          {history.map((row) => {
            return (
              <InfoContainer
                key={row.id}
                spacing={2}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack spacing={1} direction="row" alignItems="center">
                  <TwInitialsIndicator
                    text={getName(row)}
                    sx={{
                      backgroundColor: '#D4E2FC !important',
                      color: '#5C6AF6 !important'
                    }}
                  />

                  <Stack spacing={0}>
                    <Typography variant="body1" fontWeight="400" color="#1F2937" fontSize="14px">
                      {getName(row)}
                    </Typography>
                    <Typography variant="body1" fontWeight="400" color="#6B7280" fontSize="12px">
                      {getContact(row)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack spacing={0} alignItems="flex-end">
                  <Typography variant="body1" fontWeight="400" color="#6B7280" fontSize="12px">
                    {getChannelLabel(row)}
                  </Typography>
                  <Typography variant="body1" fontWeight="400" color="#1F2937" fontSize="14px">
                    {DateUtils.getFullLocaleDate(row.created_at)}
                  </Typography>
                </Stack>
              </InfoContainer>
            );
          })}
        </HistoryList>
      )}
    </>
  );
};

export default TwShareHistory;
